import { useEffect, useRef, useCallback, useState } from "react";
import { userManager, cognitoLogout } from "../../config/authConfig";
import debounce from "lodash/debounce";

const SessionChecker = () => {
  const intervalRef = useRef(null);
  const isCheckingRef = useRef(false);
  const [lastChecked, setLastChecked] = useState(Date.now());
  const isLoggedOutRef = useRef(false);

  const handleSessionExpiration = useCallback(() => {
    if (isLoggedOutRef.current) {
      console.warn("Already logged out, skipping redundant logout");
      return;
    }

    console.warn("Session expired, logging out and redirecting");
    isLoggedOutRef.current = true;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    cognitoLogout();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkSession = useCallback(
    debounce(async () => {
      if (isLoggedOutRef.current || isCheckingRef.current) {
        console.warn(
          "Skipping session check - already logged out or check in progress"
        );
        return;
      }

      try {
        console.warn("Checking session status");
        isCheckingRef.current = true;

        const user = await userManager.getUser();

        if (!user || user.expired) {
          console.warn("Session is invalid or missing");
          handleSessionExpiration();
          return;
        }

        console.warn("Session is valid");
        setLastChecked(Date.now());
      } catch (error) {
        console.error("Session check error:", error);
        handleSessionExpiration();
      } finally {
        isCheckingRef.current = false;
      }
    }, 1000),
    [handleSessionExpiration]
  );

  useEffect(() => {
    isLoggedOutRef.current = false;

    // Listen for silent renew errors
    const onSilentRenewError = () => {
      console.warn("Silent renew failed");
      handleSessionExpiration();
    };

    // Listen for token expiration — only log, let automaticSilentRenew
    // use the refresh token before forcing logout
    const onAccessTokenExpired = () => {
      console.warn("Access token expired — waiting for silent renew");
    };

    // Listen for successful token refresh
    const onUserLoaded = () => {
      console.warn("Token refreshed successfully");
      isLoggedOutRef.current = false;
      setLastChecked(Date.now());
    };

    // Listen for user removal
    const onUserUnloaded = () => {
      console.warn("User unloaded");
      isLoggedOutRef.current = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    userManager.events.addSilentRenewError(onSilentRenewError);
    userManager.events.addAccessTokenExpired(onAccessTokenExpired);
    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);

    // Initial session check
    checkSession();

    // Start interval checks
    const startIntervalCheck = () => {
      intervalRef.current = setInterval(() => {
        console.warn(
          "Running interval check, isCheckingRef:",
          isCheckingRef.current,
          "isLoggedOutRef:",
          isLoggedOutRef.current
        );
        if (!isCheckingRef.current && !isLoggedOutRef.current) {
          checkSession();
        }
      }, 60000);
    };

    if (!isLoggedOutRef.current) {
      startIntervalCheck();
    }

    // Check session when tab is focused
    const handleFocus = () => {
      if (!isLoggedOutRef.current) {
        console.warn("Window focused - checking session");
        checkSession();
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      console.warn("Cleaning up SessionChecker");
      userManager.events.removeSilentRenewError(onSilentRenewError);
      userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      window.removeEventListener("focus", handleFocus);
      checkSession.cancel();
    };
  }, [checkSession, handleSessionExpiration]);

  return (
    <div
      style={{ display: "none" }}
      data-testid="session-checker"
      data-last-checked={lastChecked}
      data-logged-out={isLoggedOutRef.current}
    >
      {/* Hidden component for testing */}
    </div>
  );
};

export default SessionChecker;
